import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader2, Flame, User, ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimezoneSelect, { detectTimezone } from '@/components/TimezoneSelect';
import { supabase } from '@/integrations/supabase/client';
import { markWelcomeSeen } from '@/pages/welcome/welcomeStorage';
import { EmailNotRegisteredModal } from '@/components/auth/EmailNotRegisteredModal';
import { emailTypoMessage } from '@/lib/emailDomainCheck';

type AuthMode = 'login' | 'forgot' | 'reset';

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, user, resetPasswordForEmail, updatePassword } = useAuth();
  const { logoUrl } = useBranding();

  // Reaching /auth (directly or via the welcome landing) means we should
  // not bounce future visits through /welcome again on this device.
  // Skip when the user landed here for password recovery — they may not
  // have ever seen the welcome flow.
  useEffect(() => {
    if (searchParams.get('mode') === 'reset') return;
    markWelcomeSeen();
  }, [searchParams]);
  
  // Detectar modo via query param
  const modeFromUrl = searchParams.get('mode');
  const [authMode, setAuthMode] = useState<AuthMode>(modeFromUrl === 'reset' ? 'reset' : 'login');
  
  // Reagir a mudanças na URL (importante para quando o Supabase redireciona)
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'reset' && authMode !== 'reset') {
      logger.log('[Auth] Detected reset mode from URL, switching to reset form');
      setAuthMode('reset');
    }
  }, [searchParams, authMode]);
  
  // Se já estiver logado e não for reset, redireciona
  useEffect(() => {
    // Se estiver no modo reset, NÃO redirecionar mesmo logado
    if (authMode === 'reset') {
      logger.log('[Auth] Reset mode active, staying on page');
      return;
    }
    
    if (user) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state, authMode]);
  
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timezone, setTimezone] = useState(() => detectTimezone());
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [notRegisteredOpen, setNotRegisteredOpen] = useState(false);
  const [loginTab, setLoginTab] = useState<'login' | 'signup'>('login');

  // Barreiras antibot locais do cadastro. O corte não depende mais da
  // Edge Function antiga de emissão de token.
  const [hp, setHp] = useState('');
  const [formOpenedAt, setFormOpenedAt] = useState<number>(() => Date.now());

  useEffect(() => {
    if (loginTab === 'signup') setFormOpenedAt(Date.now());
  }, [loginTab]);
  
  // Cooldown timer for password recovery
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);
  
  const getRedirectPath = () => {
    return (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast.error('Erro ao entrar', { description: error.message });
        return;
      }
      // Confirma que a sessão foi gravada antes de navegar — evita corrida
      // com o ProtectedRoute. Se por algum motivo não houver sessão, o
      // useEffect(user) ainda funciona como rede de segurança.
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        toast('Bem-vindo!', { description: 'Login realizado com sucesso.', duration: 2000 });
        navigate(getRedirectPath(), { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Erro', { description: 'As senhas não coincidem.' });
      return;
    }

    if (password.length < 6) {
      toast.error('Erro', { description: 'A senha deve ter pelo menos 6 caracteres.' });
      return;
    }

    const typo = emailTypoMessage(email);
    if (typo) {
      toast.error('Confira seu e-mail', { description: typo });
      return;
    }
    
    setLoading(true);
    
    const { error } = await signUp(email, password, displayName, timezone, {
      hp,
      elapsedMs: Date.now() - formOpenedAt,
    });

    if (error) {
      // Generic message to prevent email enumeration
      if (error.message?.includes('already registered') || error.message?.includes('already been registered')) {
        toast('Verifique seu e-mail', { description: 'Se este e-mail ainda não estiver cadastrado, você receberá um link de confirmação.' });
      } else {
        toast.error('Erro ao criar conta', { description: error.message });
      }
    } else {
      toast('Verifique seu e-mail', { description: 'Se este e-mail ainda não estiver cadastrado, você receberá um link de confirmação. Confira também a pasta de spam.' });
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Erro', { description: 'Digite seu e-mail.' });
      return;
    }
    
    setLoading(true);

    const { error, alreadySent } = await resetPasswordForEmail(email);
    
    if (error) {
      if (error.message?.toLowerCase().includes('rate limit')) {
        toast.error('Aguarde um momento', { description: 'Muitas tentativas. Aguarde antes de solicitar outro e-mail de recuperação.' });
        setCooldown(60);
      } else {
        toast.error('Erro', { description: error.message });
      }
    } else if (alreadySent) {
      setEmailSent(true);
      setCooldown(60);
      toast('Link já enviado', { description: 'Enviamos o link de recuperação há poucos minutos e ele continua válido. Confira sua caixa de entrada e a pasta de spam.' });
    } else {
      setEmailSent(true);
      setCooldown(60);
      toast('E-mail enviado!', { description: 'Verifique sua caixa de entrada (e a pasta de spam) para redefinir sua senha.' });
    }
    
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Erro', { description: 'As senhas não coincidem.' });
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Erro', { description: 'A senha deve ter pelo menos 6 caracteres.' });
      return;
    }
    
    setLoading(true);
    
    // skipRefresh: vamos deslogar logo abaixo, refresh é round-trip desperdiçado
    const { error } = await updatePassword(newPassword, { skipRefresh: true });
    
    if (error) {
      toast.error('Erro', { description: error.message });
      setLoading(false);
      return;
    }
    
    // IMPORTANTE: Deslogar o usuário após definir nova senha
    // Isso força ele a fazer login com a nova senha (evita magic link automático)
    logger.log('[handleResetPassword] Password updated, signing out...');
    await supabase.auth.signOut();
    
    setLoading(false);
    toast('Senha definida!', { description: 'Faça login com sua nova senha.' });
    
    // Limpar URL e voltar para login
    setAuthMode('login');
    navigate('/auth', { replace: true });
  };

  const renderLogo = () => (
    <div className="flex items-center justify-center gap-2.5 mb-8">
      <div 
        className={cn(
          "relative w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden select-none",
          !logoUrl && "bg-gradient-to-br from-primary to-primary/70 shadow-glow-sm"
        )}
      >
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt="Logo" 
            loading="eager"
            className="w-full h-full object-contain pointer-events-none" 
          />
        ) : (
          <Flame className="w-6 h-6 text-primary-foreground pointer-events-none" />
        )}
      </div>
      <span className="font-display font-bold text-2xl text-foreground tracking-tight select-none">
        Forja<span className="text-gradient-primary">Hub</span>
      </span>
    </div>
  );

  // Modo de recuperação de senha
  if (authMode === 'forgot') {
    return (
      <main className="min-h-screen py-12 px-6 bg-background flex flex-col items-center justify-center">
        <div className="w-full max-w-md">
          {renderLogo()}

          <div className="card-premium p-8">
            <div className="flex items-center gap-2 mb-6">
              <KeyRound className="w-5 h-5 text-primary" />
              <h2 className="font-display font-semibold text-lg">Recuperar Senha</h2>
            </div>

            {emailSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  Enviamos um link de recuperação para <strong className="text-foreground">{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Verifique sua caixa de entrada e spam.
                </p>
                <Button
                  variant="ghost"
                  className="mt-4"
                  onClick={() => {
                    setAuthMode('login');
                    setEmailSent(false);
                    setEmail('');
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar para o login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Digite seu e-mail para receber um link de recuperação de senha.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="forgot-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading || cooldown > 0}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : cooldown > 0 ? (
                    `Reenviar em ${cooldown}s`
                  ) : (
                    'Enviar link de recuperação'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setAuthMode('login')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar para o login
                </Button>
              </form>
            )}
          </div>
        </div>
        <EmailNotRegisteredModal
          open={notRegisteredOpen}
          email={email}
          onClose={() => setNotRegisteredOpen(false)}
          onCreateAccount={() => {
            setNotRegisteredOpen(false);
            setAuthMode('login');
            setLoginTab('signup');
          }}
        />
      </main>
    );
  }

  // Modo de definir nova senha (após clicar no link do email)
  if (authMode === 'reset') {
    return (
      <main className="min-h-screen py-12 px-6 bg-background flex flex-col items-center justify-center">
        <div className="w-full max-w-md">
          {renderLogo()}

          <div className="card-premium p-8">
            <div className="flex items-center gap-2 mb-6">
              <KeyRound className="w-5 h-5 text-primary" />
              <h2 className="font-display font-semibold text-lg">Definir Nova Senha</h2>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Digite sua nova senha abaixo.
              </p>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="Repita a nova senha"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar nova senha'
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  // Modo padrão: Login / Cadastro
  return (
    <main className="min-h-screen py-12 px-6 bg-background flex flex-col items-center justify-center">
      <h1 className="sr-only">Entrar ou criar sua conta ForjaHub</h1>
      <div className="w-full max-w-md">
        {renderLogo()}

        <div className="card-premium p-8">
          <Tabs value={loginTab} onValueChange={(v) => setLoginTab(v as 'login' | 'signup')} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Entrar</TabsTrigger>
              <TabsTrigger value="signup">Criar Conta</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => setAuthMode('forgot')}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Esqueceu sua senha?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                {/* Honeypot: invisível para humanos, preenchido por bots */}
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  className="absolute opacity-0 h-0 w-0 -z-10 pointer-events-none"
                />
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Seu nome"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use uma senha forte e única — senhas comuns ou que aparecem em vazamentos são bloqueadas.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">Confirmar Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-confirm"
                      type="password"
                      placeholder="Repita a senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <TimezoneSelect
                  value={timezone}
                  onChange={setTimezone}
                  label="Fuso Horário *"
                />

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Criando conta...
                    </>
                  ) : (
                    'Criar Conta'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

          <div className="mt-6 space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="font-semibold text-foreground mb-1">Não é aluno da Forja?</p>
              <p>Você pode explorar o Marketplace público. O acesso às áreas internas é exclusivo para alunos com produto ativo.</p>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">Já é aluno da Forja?</p>
              <p className="mb-2">
                Crie sua conta usando o <strong className="text-foreground">mesmo e-mail cadastrado na Forja</strong>. Após confirmar o e-mail, seu acesso ativo será vinculado automaticamente.
              </p>
              <ol className="list-decimal list-inside space-y-1 pl-1">
                <li>
                  Abra a aba{' '}
                  <button
                    type="button"
                    onClick={() => setLoginTab('signup')}
                    className="text-primary hover:underline cursor-pointer font-medium"
                  >
                    'Criar Conta'
                  </button>
                </li>
                <li>
                  Informe o mesmo e-mail do seu cadastro de aluno e escolha uma senha.
                </li>
                <li>
                  Confirme o endereço pelo link enviado pelo Supabase Auth.
                </li>
                <li>
                  Volte e entre; o Cérebro validará o produto “A Forja Digital - Comunidade”.
                </li>
              </ol>
              <p className="mt-2 text-xs">
                Já criou a conta e esqueceu a senha? Use “Esqueceu sua senha?”.
              </p>
            </div>
          </div>
      </div>
      <EmailNotRegisteredModal
        open={notRegisteredOpen}
        email={email}
        onClose={() => setNotRegisteredOpen(false)}
        onCreateAccount={() => {
          setNotRegisteredOpen(false);
          setAuthMode('login');
          setLoginTab('signup');
        }}
      />
    </main>
  );
}
